import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChefHat, Square, Droplets, Sofa, Wind, Layers, Flame, Building2, Check, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

// Default services data as fallback
const defaultServices = [
  { id: 'exhausts', title: 'Canopy, Duct & Rooftop Fan Cleaning', description: 'Complete kitchen exhaust system cleaning including hoods, ducts, and rooftop fans. AS 1851:2012 compliant.', icon: 'Wind', features: ['Hood cleaning', 'Duct cleaning', 'Rooftop fan', 'AS 1851:2012 compliant'] },
  { id: 'kitchen-clean', title: 'Commercial Kitchen Cleaning', description: 'Professional deep cleaning for commercial kitchens. We ensure your kitchen meets health and safety standards with thorough sanitisation.', icon: 'ChefHat', features: ['Deep cleaning', 'Sanitisation', 'Health compliance', 'Equipment cleaning'] },
  { id: 'warehouse', title: 'Warehouse & Industrial Cleaning', description: 'End-of-lease warehouse turnovers, rotary concrete floor cleaning, paint removal, and ongoing industrial facility maintenance.', icon: 'Building2', features: ['Warehouse turnovers', 'Concrete floor cleaning', 'Paint removal', 'Industrial maintenance'] },
  { id: 'office-clean', title: 'Commercial Office Cleaning', description: 'Complete office cleaning solutions including desks, floors, kitchens, and bathrooms. Keep your workplace spotless and professional.', icon: 'Layers', features: ['Daily cleaning', 'Deep cleaning', 'Bathroom sanitisation', 'Kitchen areas'] },
  { id: 'window', title: 'Commercial Window Cleaning', description: 'Crystal clear windows for commercial properties. High-rise and ground-level specialists with streak-free results.', icon: 'Square', features: ['High-rise', 'Ground level', 'Streak-free', 'All glass types'] },
  { id: 'carpet', title: 'Commercial Carpet Cleaning', description: 'Professional carpet steam cleaning and dry cleaning. Stain removal and deodorising for commercial spaces.', icon: 'Sofa', features: ['Steam cleaning', 'Dry cleaning', 'Stain removal', 'Deodorising'] },
  { id: 'pressure-wash', title: 'Commercial High-Pressure Cleaning', description: 'Industrial-strength pressure washing for driveways, facades, parking lots, carparks, and outdoor areas.', icon: 'Droplets', features: ['Driveways', 'Building facades', 'Carpark cleaning', 'Outdoor areas'] },
  { id: 'pizza-oven', title: 'Commercial Pizza Oven Cleaning', description: 'Specialised cleaning for commercial pizza ovens. Remove carbon buildup and maintain optimal performance.', icon: 'Flame', features: ['Carbon removal', 'Deep cleaning', 'Performance maintenance', 'Safety compliance'] }
];

const Services = () => {
  const [services, setServices] = useState(defaultServices);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        if (res.data && res.data.length > 0) {
          setServices(res.data);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Keep using default services
      }
    };
    fetchServices();
  }, []);

  const iconMap = {
    ChefHat: ChefHat,
    Square: Square,
    Droplets: Droplets,
    Sofa: Sofa,
    Wind: Wind,
    Layers: Layers,
    Flame: Flame,
    Building2: Building2
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services" />
        <title>Our Services | Extreme Clean Australia</title>
      </Helmet>
      <div className="pt-24 lg:pt-44" data-testid="services-page">
      {/* Hero */}
      <section className="bg-[#0A0A0A] py-24 relative" data-testid="services-hero">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1669101602124-f5b78895d91c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwY2xlYW5pbmclMjBjcmV3JTIwY29tbWVyY2lhbHxlbnwwfHx8fDE3ODg5NDg5ODJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Professional industrial cleaning"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-4">Our Services</p>
            <h1 className="font-heading font-bold text-5xl md:text-6xl leading-snug mb-6 text-white">
              Professional Cleaning Solutions
            </h1>
            <p className="text-gray-400 text-lg">
              From daily office maintenance to specialised industrial cleaning, 
              we deliver tailored solutions for every environment.
            </p>
          </div>
        </div>
      </section>

      {/* Services Navigation - Clean Grid */}
      <section className="py-16 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Building2;
              // Map service IDs to their dedicated page or anchor
              const serviceLinks = {
                'exhausts': '/services/canopy-duct-fan-cleaning-perth',
                'kitchen-clean': '/services/commercial-kitchen-cleaning-perth',
                'warehouse': '/services/warehouse-industrial-cleaning-perth',
                'office-clean': '/services/commercial-office-cleaning-perth',
                'window': '/services/commercial-window-cleaning-perth',
                'carpet': '/services/commercial-carpet-cleaning-perth',
                'pressure-wash': '/services/commercial-high-pressure-cleaning-perth',
                'pizza-oven': '/services/commercial-pizza-oven-cleaning-perth'
              };
              const linkPath = serviceLinks[service.id] || '/services';
              const isExternalPage = linkPath.startsWith('/services/') && !linkPath.includes('#');
              
              return isExternalPage ? (
                <Link
                  key={service.id}
                  to={linkPath}
                  className="bg-gray-50 p-6 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer block"
                  data-testid={`service-nav-${service.id}`}
                >
                  <div className="w-12 h-12 bg-blue-50 flex items-center justify-center mb-4">
                    <IconComponent size={24} className="text-[#3B82F6]" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{service.description}</p>
                </Link>
              ) : (
                <div
                  key={service.id}
                  className="bg-gray-50 p-6 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
                  onClick={() => {
                    const element = document.getElementById(service.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  data-testid={`service-nav-${service.id}`}
                >
                  <div className="w-12 h-12 bg-blue-50 flex items-center justify-center mb-4">
                    <IconComponent size={24} className="text-[#3B82F6]" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-24 bg-gray-50" data-testid="all-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Complete Range</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl">All Our Services</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              From daily maintenance to specialised deep cleaning, we offer comprehensive solutions for all your commercial cleaning needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building2;
              // Map service IDs to their dedicated page or stay on this page
              const serviceLinks = {
                'exhausts': '/services/canopy-duct-fan-cleaning-perth',
                'kitchen-clean': '/services/commercial-kitchen-cleaning-perth'
              };
              const linkPath = serviceLinks[service.id];
              
              const cardContent = (
                <>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-14 h-14 bg-blue-50 flex items-center justify-center">
                      <IconComponent size={28} className="text-[#3B82F6]" />
                    </div>
                    <span className="font-mono text-xs text-gray-400">0{index + 1}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check size={16} className="text-[#3B82F6] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 text-[#3B82F6] text-sm font-medium">
                    {linkPath ? 'View Details' : 'Request Quote'}
                    <ArrowRight size={16} />
                  </div>
                </>
              );
              
              return linkPath ? (
                <Link
                  key={service.id}
                  id={service.id}
                  to={linkPath}
                  className="bg-white p-8 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer block"
                  data-testid={`all-service-${service.id}`}
                >
                  {cardContent}
                </Link>
              ) : (
                <div 
                  key={service.id}
                  id={service.id}
                  className="bg-white p-8 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
                  onClick={() => window.location.href = '/contact'}
                  data-testid={`all-service-${service.id}`}
                >
                  {cardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">How It Works</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl">Our Process</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We assess your space and understand your specific cleaning requirements.' },
              { step: '02', title: 'Custom Quote', desc: 'Receive a detailed, no-obligation quote tailored to your needs.' },
              { step: '03', title: 'Scheduling', desc: 'Choose convenient cleaning times that work for your business.' },
              { step: '04', title: 'Execution', desc: 'Our trained team delivers spotless results, every time.' }
            ].map((item, index) => (
              <div key={index} className="relative" data-testid={`process-step-${item.step}`}>
                <div className="text-6xl font-heading font-bold text-[#3B82F6] opacity-20 mb-4">{item.step}</div>
                <h3 className="font-heading font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 right-0 w-full h-0.5 bg-neutral-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3B82F6]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Every property is unique. Contact us to discuss your specific cleaning requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm">
              Contact Us
            </a>
            <a href="tel:0861875523" className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm">
              08 6187 5523
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Services;
